import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { AppId } from '../client-manager/client-manager.service';
import { EventService } from '../event/event.service';
import { EncryptionService } from '@evorait/pace-encryption';
import { EncryptionKeyModel, ODataEncryptionKeyModel } from '../../models/low-level/encryption-key.model';
import { UserMasterService } from '../user-master/user-master.service';
import { OnlineOnlyService } from '../online-only/online-only.service';

@Injectable({
    providedIn: 'root',
})
export class EncryptionInitializer {
    public isInitialized = false;
    public constructor(
        private readonly onlineOnlyService: OnlineOnlyService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly eventService: EventService,
        private readonly uriUtilities: UriUtilitiesService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly encryptionService: EncryptionService,
        private readonly userMasterService: UserMasterService,
    ) {}

    public init(): Observable<void> {
        if (
            !this.userMasterService.isPaceOnlineUser() &&
            !this.clientManager.isPaceFormsFI() &&
            !this.clientManager.isPacePWA()
        ) {
            this.isInitialized = true;
            return of(undefined);
        }
        return this.getUserEncryptedKey().pipe(
            switchMap((encryptionKeys) => this.encryptionService.init(encryptionKeys.encryptedKey, AppId.paceonline)),
            tap(() => {
                this.isInitialized = this.encryptionService.isInitialized;
                this.eventService.triggerEncryptionInitialized();
            }),
        );
    }

    private getUserEncryptedKey(): Observable<EncryptionKeyModel> {
        return this.onlineOnlyService.get(ENDPOINTS.EncryptionKeySet).pipe(
            map((encryptionResponse: ODataEncryptionKeyModel[]) => {
                if (!encryptionResponse || encryptionResponse.length === 0) {
                    this.log.error('EncryptionService: getUserEncryptedKey: encryptionResponse is empty');
                    throw new Error(`EncryptionService: getUserEncryptedKey: encryptionResponse`);
                }
                const encryptionKeyModels = encryptionResponse[0];
                return new EncryptionKeyModel(encryptionKeyModels.UserName, encryptionKeyModels.EncryptedKey);
            }),
        );
    }
}
